import { useState, useEffect } from "react";
import { X, Minus, Plus, Check, AlertTriangle, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { useCart } from "@/contexts/CartContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import type { ProductDefinition, CustomizationGroup, SelectedCustomization } from "@/types/orders";

interface ProductDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (quantity: number, selectedOptions: SelectedCustomization[]) => void;
  onRemoveFromCart?: (productId: string) => void;
  product: ProductDefinition | null;
  existingQuantity?: number;
  existingOptions?: SelectedCustomization[];
}

const ProductDetailModal = ({ 
  isOpen, 
  onClose, 
  onAddToCart, 
  onRemoveFromCart,
  product,
  existingQuantity = 0,
  existingOptions = []
}: ProductDetailModalProps) => {
  const isMobile = useIsMobile();
  const { removeItem } = useCart();
  const [quantity, setQuantity] = useState(existingQuantity > 0 ? existingQuantity : 1);
  const [selectedOptions, setSelectedOptions] = useState<SelectedCustomization[]>(existingOptions);

  // Sync state when modal opens or product changes
  useEffect(() => {
    if (isOpen && product) {
      setQuantity(existingQuantity > 0 ? existingQuantity : 1);
      setSelectedOptions(existingOptions);
    }
  }, [isOpen, product?.id, existingQuantity, existingOptions]);

  if (!product) return null;

  // Convert legacy removableIngredients to customizationGroups for backward compatibility
  const legacyGroups = product.removableIngredients && product.removableIngredients.length > 0 && !product.customizationGroups
    ? [{
        id: "ingredientes",
        title: "🥗 Quitar Ingredientes",
        required: false,
        type: "checkbox" as const,
        options: product.removableIngredients.map((ing, idx) => ({
          id: `no-${ing.toLowerCase().replace(/\s+/g, '-')}`,
          label: `Sin ${ing}`,
          price: 0,
        }))
      }]
    : [];

  const groups = product.customizationGroups || legacyGroups;
  const isEditing = existingQuantity > 0;

  // Get icon for option based on content
  const getOptionIcon = (label: string): string => {
    const lowerLabel = label.toLowerCase();
    if (lowerLabel.includes("lactosa") || lowerLabel.includes("nata")) return "🥛";
    if (lowerLabel.includes("avellana") || lowerLabel.includes("almendra") || lowerLabel.includes("turrón") || lowerLabel.includes("frutos secos")) return "🥜";
    if (lowerLabel.includes("alcohol") || lowerLabel.includes("kirsch") || lowerLabel.includes("licor")) return "🍷";
    if (lowerLabel.includes("gluten")) return "🌾";
    if (lowerLabel.includes("huevo")) return "🥚";
    if (lowerLabel.includes("cereza") || lowerLabel.includes("fresa") || lowerLabel.includes("fruta")) return "🍓";
    if (lowerLabel.includes("chocolate")) return "🍫";
    if (lowerLabel.includes("merengue")) return "☁️";
    if (lowerLabel.includes("cartel") || lowerLabel.includes("felicitación") || lowerLabel.includes("dedicatoria")) return "🎂";
    if (lowerLabel.includes("extra")) return "✨";
    return "⚙️";
  };

  // Check if option is allergy-related
  const isAllergyOption = (label: string): boolean => {
    const lowerLabel = label.toLowerCase();
    return lowerLabel.includes("lactosa") || 
           lowerLabel.includes("avellana") || 
           lowerLabel.includes("almendra") || 
           lowerLabel.includes("turrón") ||
           lowerLabel.includes("gluten") ||
           lowerLabel.includes("huevo") ||
           lowerLabel.includes("alcohol");
  };

  // Toggle option selection
  const toggleOption = (group: CustomizationGroup, optionId: string, optionLabel: string, optionPrice: number) => {
    setSelectedOptions(prev => {
      const existingIndex = prev.findIndex(
        o => o.groupId === group.id && o.optionId === optionId
      );

      if (existingIndex >= 0) {
        // Remove if already selected
        return prev.filter((_, i) => i !== existingIndex);
      } else {
        // Add new selection
        if (group.type === 'radio') {
          // For radio, replace any existing selection in this group
          const filtered = prev.filter(o => o.groupId !== group.id);
          return [...filtered, {
            groupId: group.id,
            groupTitle: group.title,
            optionId,
            optionLabel,
            price: optionPrice,
          }];
        } else {
          // For checkbox, just add
          return [...prev, {
            groupId: group.id,
            groupTitle: group.title,
            optionId,
            optionLabel,
            price: optionPrice,
          }];
        }
      }
    });
  };

  // Check if option is selected
  const isOptionSelected = (groupId: string, optionId: string): boolean => {
    return selectedOptions.some(o => o.groupId === groupId && o.optionId === optionId);
  };

  // Calculate total price
  const extrasTotal = selectedOptions.reduce((sum, opt) => sum + opt.price, 0);
  const unitPrice = product.price + extrasTotal;
  const totalPrice = unitPrice * quantity;

  // Handle add/update to cart
  const handleAddToCart = () => {
    if (quantity > 0) {
      onAddToCart(quantity, selectedOptions);
    }
    setQuantity(1);
    setSelectedOptions([]);
    onClose();
  };

  // Handle remove from cart
  const handleRemoveFromCart = () => {
    if (onRemoveFromCart) {
      onRemoveFromCart(product.id);
    }
    setQuantity(1);
    setSelectedOptions([]);
    onClose();
  };

  // Content component (shared between Dialog and Drawer)
  const ModalContent = () => (
    <div className="flex flex-col h-full max-h-[80vh]">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {/* Product description */}
        {product.description && (
          <p className="text-muted-foreground text-sm mb-6">{product.description}</p>
        )}

        {/* Customization groups */}
        {groups.length > 0 ? (
          <div className="space-y-6">
            {groups.map((group) => (
              <div key={group.id} className="space-y-3">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-primary">{group.title}</h3>
                  {group.required && (
                    <Badge variant="secondary" className="text-xs">Obligatorio</Badge>
                  )}
                </div>
                <div className="space-y-2">
                  {group.options.map((option) => {
                    const isSelected = isOptionSelected(group.id, option.id);
                    const isAllergy = isAllergyOption(option.label);
                    const icon = getOptionIcon(option.label);

                    return (
                      <button
                        key={option.id}
                        onClick={() => toggleOption(group, option.id, option.label, option.price)}
                        className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all active:scale-[0.98] ${
                          isSelected
                            ? isAllergy
                              ? "border-orange-400 bg-orange-50"
                              : "border-secondary bg-secondary/10"
                            : "border-border hover:border-muted-foreground/30 bg-card"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{icon}</span>
                          <span className={`font-medium ${isSelected ? "text-primary" : "text-foreground"}`}>
                            {option.label}
                          </span>
                          {isAllergy && (
                            <AlertTriangle className="h-4 w-4 text-orange-500" />
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          {option.price > 0 && (
                            <span className={`text-sm font-medium ${isSelected ? "text-secondary" : "text-muted-foreground"}`}>
                              +{option.price.toFixed(2)}€
                            </span>
                          )}
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                            isSelected
                              ? isAllergy
                                ? "bg-orange-500 border-orange-500"
                                : "bg-secondary border-secondary"
                              : "border-muted-foreground/30"
                          }`}>
                            {isSelected && <Check className="h-4 w-4 text-white" />}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Sparkles className="h-8 w-8 mx-auto mb-2 text-secondary" />
            <p>Este producto no tiene opciones de personalización</p>
          </div>
        )}

        {/* Selected options summary */}
        {selectedOptions.length > 0 && (
          <div className="mt-6 p-4 bg-muted/50 rounded-xl">
            <h4 className="font-semibold text-primary mb-2 text-sm">Resumen de personalizaciones:</h4>
            <div className="flex flex-wrap gap-2">
              {selectedOptions.map((opt, idx) => (
                <Badge
                  key={idx}
                  variant="outline"
                  className={`${
                    isAllergyOption(opt.optionLabel)
                      ? "bg-orange-50 text-orange-700 border-orange-300"
                      : "bg-secondary/10 text-secondary border-secondary/30"
                  }`}
                >
                  {getOptionIcon(opt.optionLabel)} {opt.optionLabel}
                  {opt.price > 0 && ` (+${opt.price.toFixed(2)}€)`}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Fixed footer with quantity and add button */}
      <div className="border-t border-border bg-background p-4 mt-auto">
        <div className="flex items-center justify-between gap-4">
          {/* Quantity selector - allows 0 for removal */}
          <div className="flex items-center gap-2 bg-muted rounded-full p-1">
            <Button
              size="icon"
              variant="ghost"
              className={`h-10 w-10 rounded-full ${quantity === 1 && isEditing ? "text-destructive hover:text-destructive hover:bg-destructive/10" : ""}`}
              onClick={() => setQuantity(q => Math.max(0, q - 1))}
              disabled={quantity <= 0}
            >
              {quantity === 1 ? <Trash2 className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
            </Button>
            <span className={`w-8 text-center font-bold text-lg ${quantity === 0 ? "text-destructive" : ""}`}>
              {quantity}
            </span>
            <Button
              size="icon"
              variant="ghost"
              className="h-10 w-10 rounded-full"
              onClick={() => setQuantity(q => Math.min(10, q + 1))}
              disabled={quantity >= 10}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {/* Dynamic action button with smooth transition */}
          <Button
            size="lg"
            variant={quantity === 0 ? "destructive" : "default"}
            className={`flex-1 h-14 text-lg transition-all duration-300 ease-out ${
              quantity === 0 
                ? "animate-scale-in" 
                : "bg-secondary hover:bg-secondary/90 text-secondary-foreground"
            }`}
            onClick={quantity === 0 ? handleRemoveFromCart : handleAddToCart}
          >
            <span className="flex items-center justify-center gap-2 transition-all duration-200">
              {quantity === 0 ? (
                <>
                  <Trash2 className="h-5 w-5 animate-scale-in" />
                  <span className="animate-fade-in">Eliminar del pedido</span>
                </>
              ) : (
                <span className="animate-fade-in">
                  {isEditing ? "Actualizar" : "Añadir"} • {product.price > 0 ? `${totalPrice.toFixed(2)}€` : "Consultar precio"}
                </span>
              )}
            </span>
          </Button>
        </div>
      </div>
    </div>
  );

  // Render as Drawer on mobile, Dialog on desktop
  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader className="border-b border-border pb-4">
            <div className="flex items-center justify-between">
              <DrawerTitle className="text-xl font-bold text-primary">
                {product.name}
              </DrawerTitle>
              <Button
                size="icon"
                variant="ghost"
                className="rounded-full"
                onClick={onClose}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </DrawerHeader>
          <ModalContent />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] p-0 overflow-hidden">
        <DialogHeader className="border-b border-border p-4">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-bold text-primary">
              {product.name}
            </DialogTitle>
          </div>
        </DialogHeader>
        <ModalContent />
      </DialogContent>
    </Dialog>
  );
};

export default ProductDetailModal;
